import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatMenuModule } from '@angular/material/menu';
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs';
import { SearchService } from 'app/layout/common/search-page/search-page.service';

import { AddressPendingSyncStateService } from './services/address-pending-sync-state.service';
import { AddressFilters } from './interfaces/address-filters.interface';
import { SyncResponse, SingleSyncResponse } from './interfaces/sync-response.interface';
import { AddressStatusErpBadgeComponent } from './components/address-status-erp-badge/address-status-erp-badge.component';
import { AddressTypeTagComponent } from './components/address-type-tag/address-type-tag.component';
import { TagsDisplayComponent } from 'app/common/components/tags-display/tags-display.component';
import { ConfigurationFiltersComponent } from '../../filters/configuration-filters/configuration-filters.component';
import { BusinessPartnersFiltersComponent } from '../../filters/business-partners-filters/business-partners-filters.component';
import { RoutesFiltersComponent } from '../../filters/routes-filters/routes-filters.component';
import { MultiLocationFiltersComponent } from '../../filters/multi-location-filters/multi-location-filters.component';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { FilterSectionComponent } from 'app/common/components/filter-section/filter-section.component';

@Component({
  selector: 'app-business-partner-address-pending-sync',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatSortModule,
    MatSidenavModule,
    MatMenuModule,
    AddressStatusErpBadgeComponent,
    AddressTypeTagComponent,
    FilterComponent,
    FilterSectionComponent,
    BusinessPartnersFiltersComponent,
    RoutesFiltersComponent,
    MultiLocationFiltersComponent,
  ],
  templateUrl: './business-partner-address-pending-sync.component.html',
  styleUrl: './business-partner-address-pending-sync.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessPartnerAddressPendingSyncComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubscription: Subscription;

  // Estado local del componente
  private selectedAddressesSubject = new BehaviorSubject<string[]>([]);
  selectedAddresses$ = this.selectedAddressesSubject.asObservable();

  private currentFiltersSubject = new BehaviorSubject<AddressFilters>({});
  currentFilters$ = this.currentFiltersSubject.asObservable();

  // Form para filtros
  filterForm: FormGroup;
  searchQuery: string = '';

  // Lazy loading flags for filters
  loadTerritoryData = false;
  loadBusinessPartnerData = false;
  loadLocationData = false;

  // Paginación
  pageSize = 50;
  pageIndex = 0;

  // Columnas de la tabla
  displayedColumns: string[] = [
    'select',
    'businessPartnerCode',
    'businessPartnerName',
    'address',
    'erpStatus',
    'country',
    'department',
    'city',
    'type',
    'actions',
  ];

  constructor(
    private stateService: AddressPendingSyncStateService,
    private snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private searchService: SearchService
  ) {
    this.filterForm = this.formBuilder.group({
      customerId: [[]], // Business Partner
      routeId: [[]], // Territorio
      locationOneIds: [[]], // Países
      locationTwoIds: [[]], // Departamentos
      locationThreeIds: [[]], // Ciudades
    });
  }

  // Streams reactivos del servicio de estado - usando getters para evitar problemas de inicialización
  get addresses$() {
    return this.stateService.addresses$;
  }
  get loading$() {
    return this.stateService.loading$;
  }
  get error$() {
    return this.stateService.error$;
  }
  get totalItems$() {
    return this.stateService.totalItems$;
  }

  ngOnInit(): void {
    // Suscribirse al SearchService
    this.onHandleSearch();
    
    // Cargar direcciones iniciales
    this.loadAddresses();

    // Suscribirse a errores para mostrar snackbar
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.snackBar.open(error, 'Cerrar', {
          duration: 5000,
          panelClass: ['error-snackbar'],
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.stateService.clearState();
  }

  /**
   * Carga direcciones con los filtros actuales
   */
  loadAddresses(filters: AddressFilters = {}): void {
    // Agregar parámetros de paginación y búsqueda
    const filtersWithPagination = {
      ...filters,
      filter: this.searchQuery || undefined,
      page: this.pageIndex + 1, // La API espera page basado en 1, no en 0
      pageSize: this.pageSize,
    };
    this.currentFiltersSubject.next(filtersWithPagination);
    this.stateService.loadPendingAddresses(filtersWithPagination).subscribe();
  }

  /**
   * Carga direcciones sin limpiar selecciones (para paginación)
   */
  private loadAddressesForPagination(): void {
    const formValues = this.filterForm.value;
    const filters = this.mapFormToFilters(formValues);
    const filtersWithPagination = {
      ...filters,
      filter: this.searchQuery || undefined,
      page: this.pageIndex + 1, // La API espera page basado en 1, no en 0
      pageSize: this.pageSize,
    };
    this.currentFiltersSubject.next(filtersWithPagination);
    this.stateService.loadPendingAddresses(filtersWithPagination).subscribe();
  }

  /**
   * Maneja cambios en los filtros
   */
  onFiltersChanged(): void {
    this.pageIndex = 0; // Resetear a primera página
    const formValues = this.filterForm.value;
    const filters = this.mapFormToFilters(formValues);
    this.loadAddresses(filters);
    this.clearSelection();
  }

  /**
   * Mapea los valores del formulario a la interfaz de filtros
   */
  private mapFormToFilters(formValues: {customerId: string[], routeId: string[], locationOneIds: string[], locationTwoIds: string[], locationThreeIds: string[]}): AddressFilters {
    return {
      businessPartnerIds: formValues.customerId || [],
      territoryIds: formValues.routeId || [],
      locationOneIds: formValues.locationOneIds || [],
      locationTwoIds: formValues.locationTwoIds || [],
      locationThreeIds: formValues.locationThreeIds || [],
    };
  }

  /**
   * Maneja cambios en la selección de direcciones
   */
  onAddressesSelected(addressIds: string[]): void {
    this.selectedAddressesSubject.next(addressIds);
  }

  /**
   * Maneja el cambio de estado de un checkbox individual
   */
  onCheckboxChange(event: MatCheckboxChange, addressId: string): void {
    const currentSelected = this.selectedAddressesSubject.value;
    if (event.checked) {
      this.onAddressesSelected([...currentSelected, addressId]);
    } else {
      this.onAddressesSelected(currentSelected.filter(id => id !== addressId));
    }
  }

  /**
   * Sincroniza las direcciones seleccionadas
   */
  onSyncSelected(): void {
    const selectedIds = this.selectedAddressesSubject.value;

    if (selectedIds.length === 0) {
      this.snackBar.open('Seleccione al menos una dirección para sincronizar', 'Cerrar', {
        duration: 3000,
        panelClass: ['warning-snackbar'],
      });
      return;
    }

    this.stateService
      .syncAddresses(selectedIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (httpResponse: HttpResponse<SyncResponse>) => {
          // Verificar el éxito basándose en el status code HTTP
          const isSuccess = httpResponse.status >= 200 && httpResponse.status < 300;
          const response = httpResponse.body;

          if (isSuccess && response) {
            const successCount = response.data?.successCount || selectedIds.length;
            const errorCount = response.data?.errorCount || 0;

            if (errorCount > 0 && successCount > 0) {
              // Sincronización parcial
              this.snackBar.open(
                `⚠ Sincronización parcial: ${successCount} exitosas, ${errorCount} con errores`,
                'Cerrar',
                { duration: 5000, panelClass: ['warning-snackbar'] }
              );
            } else if (successCount > 0) {
              // Sincronización completa exitosa
              this.snackBar.open(
                `✓ ${successCount} ${successCount === 1 ? 'dirección sincronizada' : 'direcciones sincronizadas'} exitosamente`,
                'Cerrar',
                { duration: 3000, panelClass: ['success-snackbar'] }
              );
            } else {
              // Todas fallaron
              this.snackBar.open(
                `✗ Error al sincronizar direcciones`,
                'Cerrar',
                { duration: 5000, panelClass: ['error-snackbar'] }
              );
            }

            // Refrescar la lista y limpiar selección tras éxito
            if (successCount > 0) {
              this.onRefresh();
            }
            this.clearSelection();
          } else {
            // Error basado en status code HTTP o respuesta vacía
            const errorMessage = response?.message || `Error HTTP ${httpResponse.status}: ${httpResponse.statusText}`;
            this.snackBar.open(
              `✗ ${errorMessage}`,
              'Cerrar',
              { duration: 5000, panelClass: ['error-snackbar'] }
            );
          }
        },
        error: error => {
          console.error('Error in sync operation:', error);
          this.snackBar.open('Error inesperado en la sincronización', 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar'],
          });
        },
      });
  }

  /**
   * Sincroniza una sola dirección
   */
  onSyncSingle(addressId: string): void {
    if (!addressId) {
      return;
    }

    this.stateService
      .syncSingleAddress(addressId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (httpResponse: HttpResponse<SingleSyncResponse>) => {
          // Verificar el éxito basándose en el status code HTTP
          const isSuccess = httpResponse.status >= 200 && httpResponse.status < 300;
          const response = httpResponse.body;

          if (isSuccess && response) {
            const address = this.stateService.getAddressById(addressId);
            const businessPartnerCode = address?.businessPartnerCode || '';

            // Mensaje de éxito con información del socio
            const successMessage = businessPartnerCode
              ? `✓ Dirección sincronizada exitosamente (${businessPartnerCode})`
              : `✓ Dirección sincronizada exitosamente`;

            this.snackBar.open(
              successMessage,
              'Cerrar',
              { duration: 3000, panelClass: ['success-snackbar'] }
            );

            // Refrescar la lista para mostrar los cambios
            this.onRefresh();
          } else {
            // Error basado en status code HTTP o respuesta vacía
            const errorMessage = response?.message || `Error HTTP ${httpResponse.status}: ${httpResponse.statusText}`;
            this.snackBar.open(
              `✗ ${errorMessage}`,
              'Cerrar',
              { duration: 5000, panelClass: ['error-snackbar'] }
            );
          }
        },
        error: error => {
          console.error('Error in single sync operation:', error);
          this.snackBar.open('Error inesperado al sincronizar dirección', 'Cerrar', {
            duration: 5000,
            panelClass: ['error-snackbar'],
          });
        },
      });
  }


  /**
   * Refresca la lista de direcciones
   */
  onRefresh(): void {
    // Resetear el formulario a sus valores iniciales vacíos
    this.filterForm.reset({
      customerId: [],
      routeId: [],
      locationOneIds: [],
      locationTwoIds: [],
      locationThreeIds: [],
    });

    this.pageIndex = 0; // Resetear a primera página
    const formValues = this.filterForm.value;
    const filters = this.mapFormToFilters(formValues);
    this.loadAddresses(filters);
    this.clearSelection();
  }

  /**
   * Maneja la suscripción al SearchService
   */
  onHandleSearch(): void {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(
      (term) => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.pageIndex = 0; // Resetear a primera página
          const formValues = this.filterForm.value;
          const filters = this.mapFormToFilters(formValues);
          this.loadAddresses(filters);
          this.clearSelection();
        }
      }
    );
  }

  /**
   * Limpia la selección actual
   */
  private clearSelection(): void {
    this.selectedAddressesSubject.next([]);
  }

  /**
   * Obtiene el conteo de direcciones seleccionadas
   */
  getSelectedCount(): number {
    return this.selectedAddressesSubject.value.length;
  }

  /**
   * Verifica si hay direcciones seleccionadas
   */
  hasSelection(): boolean {
    return this.selectedAddressesSubject.value.length > 0;
  }

  /**
   * Maneja el cambio de página (preserva selecciones)
   */
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.loadAddressesForPagination();
  }

  /**
   * Maneja la expansión de la sección de Territorio
   */
  onTerritoryExpanded(): void {
    this.loadTerritoryData = true;
  }

  /**
   * Maneja la expansión de la sección de Socios de Negocio
   */
  onBusinessPartnerExpanded(): void {
    this.loadBusinessPartnerData = true;
  }

  /**
   * Maneja la expansión de la sección de Ubicación Geográfica
   */
  onLocationExpanded(): void {
    this.loadLocationData = true;
  }
}
